import React, { useEffect, useState, useRef } from 'react';
import axios from "../lib/axios";
import { useNavigate } from 'react-router-dom';
import IntellendLogo from "@/components/IntellendLogo";
import { API_BASE_URL } from '../config';
import { useJourney } from "@/contexts/JourneyContext";
import FullScreenLoader from "@/components/FullScreenLoader";
import { useToast } from "@/components/ui/use-toast";

const SelfieAadhaarMatch = ({ isBuilderMode = false }: { isBuilderMode?: boolean }) => {
  const [loading, setLoading] = useState(true);
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [matchData, setMatchData] = useState<any>(null);
  const navigate = useNavigate();
  const { refresh: refreshJourney } = useJourney();
  const { toast } = useToast();
  const hasMatched = useRef(false);

  useEffect(() => {
    if (isBuilderMode) return;
    if (hasMatched.current) return;
    hasMatched.current = true;

    const performFaceMatch = async () => {
      try {
        // const res = await axios.post(`${API_BASE_URL}/api/selfie_aadhaar_match/`);
        const res = await axios.post(`${API_BASE_URL}/api/selfie_aadhaar_match_new/`);
        if (res.data.success) {
          setMatchData(res.data.match_data);
          
          // Check if debug mode is enabled
          if (res.data.debug_mode && res.data.debug_info) {
            setDebugMode(true);
            setDebugInfo(res.data.debug_info);
          } else {
            // Normal flow - backend already advanced stage; reload /loan-application
            await refreshJourney();
            window.location.href = "/loan-application";
          }
        }
      } catch (err: any) {
        console.error("Match error:", err);
        toast({
          variant: "destructive",
          title: "Verification Failed",
          description: err.response?.data?.error || "An error occurred during face matching",
        });
        // We can stay on the screen or redirect after delay
        setTimeout(() => {
          navigate('/home');
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    performFaceMatch();
  }, [navigate, refreshJourney, toast]);

  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">🤳</div>
        <p className="font-semibold text-gray-500">Selfie Aadhaar Match</p>
        <p className="text-xs mt-1">Auto-triggers selfie vs Aadhaar face-match verification during the journey.</p>
      </div>
    );
  }

  if (debugMode && debugInfo) {
    return (
      <div className="min-h-screen bg-intellend-white flex flex-col">
        <header className="bg-intellend-green p-4">
          <div className="container mx-auto flex justify-center">
            <div className="bg-intellend-white px-2 py-1 rounded-md">
              <IntellendLogo size="sm" />
            </div>
          </div>
        </header>

        <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
          <div className="bg-white shadow-md rounded-lg p-8">
            <h2 className="text-2xl font-bold text-intellend-green mb-8 text-center">
              Face Match Debug - Images & Response
            </h2>

            {/* Images Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="flex flex-col items-center">
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Selfie Image</h3>
                {debugInfo.selfie_image && (
                  <img 
                    src={debugInfo.selfie_image} 
                    alt="Selfie" 
                    className="max-w-full h-auto rounded-lg shadow-md border-2 border-intellend-green"
                  />
                )}
              </div>
              <div className="flex flex-col items-center">
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Aadhaar Image</h3>
                {debugInfo.aadhaar_image && (
                  <img 
                    src={debugInfo.aadhaar_image} 
                    alt="Aadhaar" 
                    className="max-w-full h-auto rounded-lg shadow-md border-2 border-intellend-green"
                  />
                )}
              </div>
            </div>

            {/* Response Section */}
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">API Response</h3>
              <pre className="bg-gray-800 text-green-400 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                {debugInfo.response_text}
              </pre>
            </div>

            {/* Match Data Summary */}
            {matchData && (
              <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-700 mb-4">Match Summary</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Match Result</p>
                    <p className="text-lg font-bold text-blue-900">
                      {matchData.match_result || matchData.result || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Match Percentage</p>
                    <p className="text-lg font-bold text-blue-900">
                      {matchData.confidence !== null && matchData.confidence !== undefined
                      ? `${matchData.confidence}%`
                      : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="mt-8 flex gap-4 justify-center">
              <button
                onClick={() => navigate('/home')}
                className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                Back to Home
              </button>
              <button
                onClick={async () => {
                  await refreshJourney();
                  navigate('/home');
                }}
                className="px-6 py-2 bg-intellend-green hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-intellend-white flex flex-col">
      <header className="bg-intellend-green p-4">
        <div className="container mx-auto flex justify-center">
          <div className="bg-intellend-white px-2 py-1 rounded-md">
            <IntellendLogo size="sm" />
          </div>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-8 max-w-3xl flex flex-col items-center justify-center">
        <div className="bg-white shadow-md rounded-lg p-8 w-full text-center">
          <h2 className="text-2xl font-bold text-intellend-green mb-6">
            Verifying Your Identity
          </h2>

          <div className="flex flex-col items-center justify-center space-y-4 min-h-[200px]">
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-intellend-green"></div>
                <p className="text-gray-600">Please wait while we securely match your selfie with your Aadhaar photo...</p>
              </>
            ) : (
              <p className="text-gray-600">Verification complete. Redirecting...</p>
            )}
          </div>
        </div>
      </main>

      <FullScreenLoader loading={loading} />
    </div>
  );
};

export default SelfieAadhaarMatch;
